import { useEffect, useState } from 'react';
import { AlertTriangle, ShieldCheck, UserCheck, UserX } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, Checkbox, Select, Separator } from '@/components/ui/misc';
import { Can } from '@/components/shared/can';
import { FormError } from '@/features/auth/auth-layout';
import { useImpersonate } from '@/features/auth/use-session';
import { usePermission } from '@/hooks/use-permission';
import { ApiRequestError } from '@/lib/api';
import { formatDate, timeAgo } from '@/lib/format';
import type { Role, UserRow } from '@/lib/types';
import {
  useAssignRoles,
  useDeactivateUser,
  useReactivateUser,
  useReassignmentCandidates,
  useUpdateUser,
} from './api';

type Tab = 'profile' | 'access' | 'danger';

export function UserDetailDialog({
  user,
  roles,
  onOpenChange,
}: {
  user: UserRow | null;
  roles: Role[];
  onOpenChange: (open: boolean) => void;
}) {
  const [tab, setTab] = useState<Tab>('profile');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setTab('profile');
      setError(null);
    }
  }, [user]);

  if (!user) return null;

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <Avatar name={user.name} size={44} />
            <div className="min-w-0">
              <DialogTitle className="truncate">{user.name}</DialogTitle>
              <DialogDescription className="truncate">
                {user.email}
                {user.designation ? ` · ${user.designation}` : ''}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex gap-1 border-b">
          {(
            [
              ['profile', 'Profile'],
              ['access', 'Access'],
              ['danger', 'Account'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={
                tab === key
                  ? 'border-b-2 border-primary px-3 py-2 text-sm font-medium'
                  : 'px-3 py-2 text-sm text-muted-foreground hover:text-foreground'
              }
            >
              {label}
            </button>
          ))}
        </div>

        <FormError message={error} />

        {tab === 'profile' && <ProfileTab user={user} onError={setError} />}
        {tab === 'access' && <AccessTab user={user} roles={roles} onError={setError} />}
        {tab === 'danger' && (
          <AccountTab user={user} onError={setError} onDone={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Profile ──────────────────────────────────────────────────────────────────

function ProfileTab({ user, onError }: { user: UserRow; onError: (m: string | null) => void }) {
  const update = useUpdateUser();
  const { can } = usePermission();
  const canSeeCost = can('finance.cost.view');

  const [form, setForm] = useState({
    name: user.name,
    phone: user.phone ?? '',
    designation: user.designation ?? '',
    department: user.department ?? '',
    employmentType: user.employmentType,
    weeklyCapacity: user.weeklyCapacity,
    billingRate: user.billingRate ?? '',
    costRate: user.costRate ?? '',
    rateCurrency: user.rateCurrency,
  });

  const save = async () => {
    onError(null);
    try {
      await update.mutateAsync({
        id: user.id,
        name: form.name,
        phone: form.phone,
        designation: form.designation,
        department: form.department,
        employmentType: form.employmentType,
        weeklyCapacity: Number(form.weeklyCapacity),
        billingRate: form.billingRate || undefined,
        ...(canSeeCost ? { costRate: form.costRate || undefined } : {}),
        rateCurrency: form.rateCurrency,
      });
    } catch (caught) {
      onError(caught instanceof ApiRequestError ? caught.message : 'Could not save.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Full name">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Phone">
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </Field>
        <Field label="Job title">
          <Input
            value={form.designation}
            onChange={(e) => setForm({ ...form, designation: e.target.value })}
          />
        </Field>
        <Field label="Department">
          <Input
            value={form.department}
            onChange={(e) => setForm({ ...form, department: e.target.value })}
          />
        </Field>
        <Field label="Employment type">
          <Select
            value={form.employmentType}
            onChange={(e) =>
              setForm({ ...form, employmentType: e.target.value as UserRow['employmentType'] })
            }
          >
            <option value="FULL_TIME">Full time</option>
            <option value="PART_TIME">Part time</option>
            <option value="CONTRACT">Contract</option>
            <option value="INTERN">Intern</option>
            <option value="VENDOR">Vendor</option>
          </Select>
        </Field>
        <Field label="Weekly capacity (hours)">
          <Input
            type="number"
            min={0}
            max={80}
            value={form.weeklyCapacity}
            onChange={(e) => setForm({ ...form, weeklyCapacity: Number(e.target.value) })}
          />
        </Field>
        <Field label={`Billing rate (${form.rateCurrency} / hour)`}>
          <Input
            inputMode="decimal"
            placeholder="0.00"
            value={form.billingRate}
            onChange={(e) => setForm({ ...form, billingRate: e.target.value })}
          />
        </Field>
        {canSeeCost && (
          <Field
            label={`Cost rate (${form.rateCurrency} / hour)`}
            hint="Only people with finance.cost.view can see this."
          >
            <Input
              inputMode="decimal"
              placeholder="0.00"
              value={form.costRate}
              onChange={(e) => setForm({ ...form, costRate: e.target.value })}
            />
          </Field>
        )}
      </div>

      <Separator />

      <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
        <Meta label="Joined" value={formatDate(user.joiningDate)} />
        <Meta label="Last seen" value={user.lastLoginAt ? timeAgo(user.lastLoginAt) : 'Never'} />
        <Meta label="Two-factor" value={user.twoFactorEnabled ? 'On' : 'Off'} />
        <Meta label="Manager" value={user.manager?.name ?? '—'} />
        <Meta label="Teams" value={user.teams.map((t) => t.name).join(', ') || '—'} />
        <Meta label="Timezone" value={user.timezone} />
      </dl>

      <Can permission="admin.user.update">
        <DialogFooter>
          <Button onClick={save} disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </Can>
    </div>
  );
}

// ── Access ───────────────────────────────────────────────────────────────────

function AccessTab({
  user,
  roles,
  onError,
}: {
  user: UserRow;
  roles: Role[];
  onError: (m: string | null) => void;
}) {
  const assign = useAssignRoles();
  const [selected, setSelected] = useState(user.roles.map((r) => r.id));

  const toggle = (roleId: string) =>
    setSelected((current) =>
      current.includes(roleId) ? current.filter((id) => id !== roleId) : [...current, roleId],
    );

  const save = async () => {
    onError(null);
    try {
      await assign.mutateAsync({ id: user.id, roleIds: selected });
    } catch (caught) {
      onError(caught instanceof ApiRequestError ? caught.message : 'Could not update roles.');
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Effective permissions are the union of every role. Where two roles grant the same permission
        at different scopes, the wider scope wins.
      </p>

      <div className="scrollbar-thin max-h-72 space-y-1 overflow-y-auto rounded-md border p-2">
        {roles.map((role) => (
          <label
            key={role.id}
            className="flex cursor-pointer items-start gap-2.5 rounded-md p-2 hover:bg-accent"
          >
            <Checkbox
              checked={selected.includes(role.id)}
              onCheckedChange={() => toggle(role.id)}
              className="mt-0.5"
            />
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2">
                <span className="text-sm font-medium">{role.name}</span>
                {role.isSystem && (
                  <Badge variant="secondary" className="text-[10px]">
                    System
                  </Badge>
                )}
              </span>
              {role.description && (
                <span className="block text-xs text-muted-foreground">{role.description}</span>
              )}
              <span className="mt-0.5 block text-[11px] text-muted-foreground">
                {role.permissions.length} permissions
              </span>
            </span>
          </label>
        ))}
      </div>

      <Can permission="admin.user.assign">
        <DialogFooter>
          <Button onClick={save} disabled={assign.isPending}>
            {assign.isPending ? 'Saving…' : 'Save roles'}
          </Button>
        </DialogFooter>
      </Can>
    </div>
  );
}

// ── Account actions ──────────────────────────────────────────────────────────

function AccountTab({
  user,
  onError,
  onDone,
}: {
  user: UserRow;
  onError: (m: string | null) => void;
  onDone: () => void;
}) {
  const deactivate = useDeactivateUser();
  const reactivate = useReactivateUser();
  const impersonate = useImpersonate();
  const { data: candidates = [] } = useReassignmentCandidates(user.id);
  const [reassignTo, setReassignTo] = useState('');
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);

  const isDeactivated = user.status === 'DEACTIVATED';

  const run = async (action: () => Promise<unknown>, failure: string) => {
    onError(null);
    try {
      await action();
      onDone();
    } catch (caught) {
      onError(caught instanceof ApiRequestError ? caught.message : failure);
    }
  };

  return (
    <div className="space-y-5">
      <Can permission="admin.impersonate.create">
        <section className="rounded-md border p-4">
          <h3 className="flex items-center gap-2 text-sm font-medium">
            <UserCheck className="h-4 w-4" />
            Sign in as this user
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            See exactly what they see. A banner stays visible the whole time and every action is
            recorded in the audit log against both of you.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            disabled={isDeactivated || impersonate.isPending}
            onClick={() => impersonate.mutate({ userId: user.id })}
          >
            Start impersonating
          </Button>
        </section>
      </Can>

      <Can permission="admin.user.update">
        <section className="rounded-md border border-destructive/30 p-4">
          <h3 className="flex items-center gap-2 text-sm font-medium">
            {isDeactivated ? (
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
            ) : (
              <UserX className="h-4 w-4 text-destructive" />
            )}
            {isDeactivated ? 'Reactivate account' : 'Deactivate account'}
          </h3>

          {isDeactivated ? (
            <>
              <p className="mt-1 text-xs text-muted-foreground">
                They will be able to sign in again with their existing password.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                disabled={reactivate.isPending}
                onClick={() => void run(() => reactivate.mutateAsync(user.id), 'Could not reactivate.')}
              >
                {reactivate.isPending ? 'Reactivating…' : 'Reactivate'}
              </Button>
            </>
          ) : (
            <>
              <p className="mt-1 text-xs text-muted-foreground">
                Their sessions end immediately. All of their history is kept.
              </p>

              <div className="mt-3 space-y-3">
                <Field
                  label="Hand their open work to"
                  hint="Deals, tasks, tickets and accounts they own. Leave blank to leave them unassigned."
                >
                  <Select value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}>
                    <option value="">Do not reassign</option>
                    {candidates.map((candidate) => (
                      <option key={candidate.id} value={candidate.id}>
                        {candidate.name}
                        {candidate.designation ? ` — ${candidate.designation}` : ''}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field label="Reason">
                  <Input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Optional — stored in the audit log"
                  />
                </Field>
              </div>

              {confirming ? (
                <div className="mt-3 rounded-md bg-destructive/10 p-3">
                  <p className="flex items-start gap-2 text-xs text-destructive">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    This signs {user.name} out everywhere straight away. Continue?
                  </p>
                  <div className="mt-3 flex gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={deactivate.isPending}
                      onClick={() =>
                        void run(
                          () =>
                            deactivate.mutateAsync({
                              id: user.id,
                              ...(reassignTo ? { reassignToUserId: reassignTo } : {}),
                              ...(reason ? { reason } : {}),
                            }),
                          'Could not deactivate.',
                        )
                      }
                    >
                      {deactivate.isPending ? 'Deactivating…' : 'Yes, deactivate'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setConfirming(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="destructive"
                  size="sm"
                  className="mt-3"
                  onClick={() => setConfirming(true)}
                >
                  Deactivate account
                </Button>
              )}
            </>
          )}
        </section>
      </Can>
    </div>
  );
}

// ── Small helpers ────────────────────────────────────────────────────────────

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="truncate font-medium">{value}</dd>
    </div>
  );
}
